/**
 * Werkzeuge, die es nur fuer Ton gibt (7.22).
 *
 * Sascha am 4. September: „Voiceover / Audios haben ja gerade dieselben
 * Optionen wie ein Bild/Video“. Ein Tonclip braucht keinen Bildausschnitt und
 * keine Farbe -- er braucht Pegel, Blenden, und zwei Handgriffe, die man sonst
 * von Hand macht: lauter ziehen, bis es passt, und die Stille vorn und hinten
 * wegschneiden.
 *
 * Beides rechnet aus der Wellenform, die beim Einlesen ohnehin entsteht
 * (`buildWaveform` im Arbeitsdienst): je Eimer die groesste Auslenkung,
 * 0…1. Keine neue Analyse, kein zweiter Weg durch die Datei -- und deshalb
 * eine Spitzen-Normalisierung, keine nach Lautheit. Das ist ein Unterschied,
 * den man hoert, und er gehoert auf den Schirm, wo der Knopf steht.
 */
/** Die Eimer, die zwischen zwei Zeiten liegen. */
export function ausschnitt(welle, vonSek, bisSek) {
    if (!welle.peaks.length || welle.durationSec <= 0)
        return [];
    const proSekunde = welle.peaks.length / welle.durationSec;
    const von = Math.max(0, Math.floor(vonSek * proSekunde));
    const bis = Math.min(welle.peaks.length, Math.ceil(bisSek * proSekunde));
    return bis > von ? welle.peaks.slice(von, bis) : [];
}
/** Die lauteste Stelle darin. */
export function spitze(peaks) {
    let hoch = 0;
    for (const p of peaks)
        if (p > hoch)
            hoch = p;
    return hoch;
}
/**
 * Der Pegel, mit dem die lauteste Stelle auf `ziel` landet.
 *
 * `ziel` liegt bei 0,89 -- etwa −1 dB. Nicht bei 1: was danach noch
 * dazukommt (der Spurpegel, eine zweite Spur daneben) hat sonst keinen Platz
 * mehr, und ein Ton, der beim Export anschlaegt, klingt kaputt.
 *
 * Stille laesst sich nicht normalisieren: `null` heisst, es gibt nichts zu
 * verstaerken. Sonst waere das Ergebnis ein Faktor gegen unendlich.
 */
export function normalPegel(peaks, ziel = 0.89, max = 4) {
    const hoch = spitze(peaks);
    if (hoch < 0.001)
        return null;
    return Math.min(max, Math.round((ziel / hoch) * 100) / 100);
}
/**
 * **Der Pegel, den ein neuer Clip von selbst bekommt** (7.210), oder `null`,
 * wenn er so bleiben soll: nur ein Clip, an dem noch niemand gedreht hat
 * (Pegel 1, kein Verlauf, nicht stumm), und nur, wenn sich etwas ändert.
 * Ein Pegel, den jemand eingestellt hat, ist eine Entscheidung; die
 * überschreibt kein Automatismus.
 */
export function autoPegel(clip, welle) {
    if (clip.gain !== 1 || clip.muted || (clip.gainKeyframes ?? []).length > 0)
        return null;
    const pegel = normalPegel(ausschnitt(welle, clip.sourceIn, clip.sourceIn + clip.duration * clip.speed));
    if (pegel === null || Math.abs(pegel - 1) < 0.01)
        return null;
    return pegel;
}
/**
 * Wie viel Stille vorn und hinten steht.
 *
 * `schwelle` ist bewusst niedrig (0,015, etwa −36 dB): ein Raum ist nie ganz
 * still, aber ein Atmer vor dem ersten Wort gehoert dazu und darf nicht
 * wegfallen. `luft` laesst deshalb einen Rest stehen -- ein Schnitt genau auf
 * das erste Wort klingt abgehackt.
 *
 * `null`, wenn ueberall Stille ist: dann waere „wegschneiden“ dasselbe wie
 * „loeschen“, und das ist eine andere Handlung.
 */
export function stilleRaender(welle, vonSek, bisSek, schwelle = 0.015, luft = 0.08) {
    const teil = ausschnitt(welle, vonSek, bisSek);
    if (!teil.length)
        return null;
    let erster = teil.findIndex((p) => p >= schwelle);
    if (erster < 0)
        return null;
    let letzter = teil.length - 1;
    while (letzter > erster && teil[letzter] < schwelle)
        letzter--;
    const sekundeProEimer = (bisSek - vonSek) / teil.length;
    const vorne = Math.max(0, erster * sekundeProEimer - luft);
    const hinten = Math.max(0, (teil.length - 1 - letzter) * sekundeProEimer - luft);
    // Auf Hundertstel: alles darunter ist unter der Wahrnehmung und macht aus
    // einem sauberen Schnitt eine krumme Zahl im Blatt.
    const runde = (v) => Math.round(v * 100) / 100;
    const ergebnis = { vorne: runde(vorne), hinten: runde(hinten) };
    return ergebnis.vorne === 0 && ergebnis.hinten === 0 ? null : ergebnis;
}
/**
 * Was sich am Clip aendert, wenn die Stille wegfaellt.
 *
 * Der Clip rueckt nicht an eine andere Stelle im Schnitt -- er faengt spaeter
 * an zu klingen und hoert frueher auf. Ihn zu verschieben wuerde alles
 * verruecken, was danach liegt.
 *
 * `speed` steht dazwischen: `sourceIn` zaehlt in der Zeit der Datei, `start`
 * und `duration` in der des Schnitts. Bei doppeltem Tempo ist eine Sekunde
 * Stille in der Datei eine halbe im Schnitt.
 */
export function nachStilleSchnitt(clip, raender, mindestens = 0.1) {
    const tempo = clip.speed > 0 ? clip.speed : 1;
    const wegImSchnitt = (raender.vorne + raender.hinten) / tempo;
    const duration = Math.max(mindestens, Math.round((clip.duration - wegImSchnitt) * 1000) / 1000);
    return {
        sourceIn: Math.round((clip.sourceIn + raender.vorne) * 1000) / 1000,
        start: Math.round((clip.start + raender.vorne / tempo) * 1000) / 1000,
        duration,
    };
}
/**
 * **Mehrere Clips zugleich lauter oder leiser** (7.209; Sascha, 4. Oktober:
 * „Lautstärke settings zusammen für alle Clips am Desktop“). Um dieselben dB
 * für jeden -- nicht auf denselben Wert: ein leiser Sprecher und laute Musik
 * bleiben zueinander, wie sie waren. Ein Pegelverlauf wandert als Ganzes mit.
 * Begrenzt wie im Schema (0 … 4).
 */
export function lauterUm(clip, db) {
    const f = Math.pow(10, db / 20);
    const um = (g) => Math.min(4, Math.max(0, Math.round(g * f * 1000) / 1000));
    const punkte = clip.gainKeyframes ?? [];
    return punkte.length > 0 ? { gain: um(clip.gain), gainKeyframes: punkte.map((k) => ({ ...k, gain: um(k.gain) })) } : { gain: um(clip.gain) };
}
//# sourceMappingURL=ton.js.map